import { supabase } from '../supabase/supabaseClient';

/**
 * Patient Service
 * Handles patient creation and management with anonymous access codes
 * HIPAA compliant - patients have no identifiable usernames
 */
class PatientService {
  constructor() {
    this.isEnabled = this.checkSupabaseConfig();
    
    if (this.isEnabled) {
      console.log('🏥 Patient Service initialized');
    } else {
      console.warn('⚠️ Patient Service disabled - using mock data');
    }
  }

  checkSupabaseConfig() {
    try {
      return !!(process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_KEY);
    } catch (error) {
      return false;
    }
  }

  /**
   * Create a new patient with access code
   * Used by clinicians to create patients
   */
  async createPatient(firstName, lastInitial, roomNumber, bedNumber, clinicianId) {
    if (!this.isEnabled) {
      // Mock mode - generate local access code
      const accessCode = Math.floor(100000 + Math.random() * 900000).toString();
      const mockId = `mock_patient_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      return {
        success: true,
        accessCode,
        patientId: mockId
      };
    }

    try {
      const { data, error } = await supabase.rpc('create_patient_with_access_code', {
        first_name: firstName,
        last_initial: lastInitial,
        room_number: roomNumber,
        bed_number: bedNumber || '',
        clinician_user_id: clinicianId
      });

      if (error) throw error;

      console.log('🏥 Patient created with access code:', data[0].out_access_code || data[0].access_code);
      return {
        success: true,
        accessCode: data[0].out_access_code || data[0].access_code,
        patientId: data[0].out_patient_id || data[0].patient_id
      };

    } catch (error) {
      console.error('Patient Service: Failed to create patient', error);
      throw new Error('Failed to create patient: ' + error.message);
    }
  }

  /**
   * Get all patients for a clinician
   */
  async getClinicianPatients(clinicianId) {
    // Force mock data when clinicianId is 'mock' or Supabase is not enabled
    if (!this.isEnabled || clinicianId === 'mock') {
      // Return mock data with realistic timestamps - 30 patients to test pagination
      const now = Date.now();
      const mockPatients = [];
      const firstNames = ['John', 'Mary', 'Robert', 'Patricia', 'Michael', 'Linda', 'William', 'Elizabeth', 'David', 'Jennifer'];
      const lastInitials = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'J', 'K'];

      // Generate 30 mock patients
      for (let i = 1; i <= 30; i++) {
        const isActive = i <= 25; // First 25 active, last 5 discharged
        const hoursAgo = Math.floor(Math.random() * 72) + 1; // Random 1-72 hours
        const daysAgo = Math.floor(Math.random() * 90) + 1; // Random 1-90 days

        mockPatients.push({
          id: `mock_patient_${i}`, // Use string IDs that look like UUIDs for consistency
          firstName: firstNames[i % 10],
          lastInitial: lastInitials[i % 10],
          roomNumber: `${100 + i}${i % 3 === 0 ? 'A' : i % 3 === 1 ? 'B' : ''}`,
          bedNumber: i % 4 === 0 ? '' : `${(i % 3) + 1}`,
          accessCode: `${100000 + i}`,
          status: isActive ? 'active' : 'discharged',
          assignedExercises: Math.floor(Math.random() * 8) + 3,
          completedExercises: Math.floor(Math.random() * 8),
          lastActive: new Date(now - hoursAgo * 60 * 60 * 1000).toISOString(),
          therapyFrequency: i % 3 === 0 ? '15 min/day × 7 days/week' : i % 3 === 1 ? '20 min/day × 3 days/week' : '15 min/day × 5 days/week',
          createdAt: new Date(now - daysAgo * 24 * 60 * 60 * 1000).toISOString()
        });
      }

      console.log('PatientService: Returning 30 mock patients for testing');
      return mockPatients;
    }

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('clinician_id', clinicianId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Normalize field names from database (lowercase) to JavaScript (camelCase)
      const normalizedData = (data || []).map(patient => ({
        id: patient.id,
        firstName: patient.firstname,
        lastInitial: patient.lastinitial,
        roomNumber: patient.roomnumber,
        bedNumber: patient.bednumber,
        accessCode: patient.access_code,
        status: patient.status || 'active',
        assignedExercises: patient.assignedexercises || 0,
        completedExercises: patient.completedexercises || 0,
        lastActive: patient.lastactive,
        createdAt: patient.created_at,
        clinician_id: patient.clinician_id,
        therapyFrequency: patient.therapy_frequency || '15 min/day × 7 days/week'
      }));

      return normalizedData;

    } catch (error) {
      console.error('Patient Service: Failed to get patients', error);
      return [];
    }
  }

  /**
   * Verify patient access code
   */
  async verifyAccessCode(accessCode) {
    if (!this.isEnabled) {
      // Mock mode - accept any 6-digit code
      if (/^\d{6}$/.test(accessCode)) {
        return {
          success: true,
          patientId: `mock_patient_${accessCode}`,
          patientName: 'Test Patient'
        };
      }
      return { success: false };
    }

    try {
      // First check if patient exists and is active
      const { data: patientData, error: patientError } = await supabase
        .from('patients')
        .select('*')
        .eq('access_code', accessCode)
        .single();

      if (patientError || !patientData) {
        return { success: false, message: 'Invalid access code' };
      }

      // Check if patient is discharged
      if (patientData.status === 'discharged') {
        return {
          success: false,
          message: 'This access code has been deactivated. Please contact your clinician.'
        };
      }

      const { data, error } = await supabase.rpc('verify_patient_access_code', {
        access_code_input: accessCode
      });

      if (error) throw error;

      if (data && data.length > 0) {
        return {
          success: true,
          patientId: data[0].patient_id,
          patientName: data[0].patient_name,
          roomNumber: data[0].room_number
        };
      }

      return { success: false };

    } catch (error) {
      console.error('Patient Service: Access code verification failed', error);
      return { success: false };
    }
  }

  /**
   * Update patient status
   */
  async updatePatientStatus(patientId, status) {
    if (!this.isEnabled) return { success: true };

    try {
      const { error } = await supabase
        .from('patients')
        .update({
          status,
          lastactive: new Date().toISOString()
        })
        .eq('id', patientId);

      if (error) throw error;

      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to update status', error);
      return { success: false };
    }
  }

  /**
   * Update patient's last active timestamp
   */
  async updatePatientLastActive(patientId) {
    if (!this.isEnabled) return { success: true };

    try {
      const { error } = await supabase
        .from('patients')
        .update({
          lastactive: new Date().toISOString()
        })
        .eq('id', patientId);

      if (error) throw error;

      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to update last active', error);
      return { success: false };
    }
  }

  /**
   * Discharge a patient (deactivate their access code)
   */
  async dischargePatient(patientId) {
    if (!this.isEnabled) {
      console.log('🏥 Mock mode: Patient discharged');
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('patients')
        .update({
          status: 'discharged',
          lastactive: new Date().toISOString()
        })
        .eq('id', patientId);

      if (error) throw error;

      console.log('🏥 Patient discharged successfully');
      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to discharge patient', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Reactivate a discharged patient
   */
  async reactivatePatient(patientId) {
    if (!this.isEnabled) {
      console.log('🏥 Mock mode: Patient reactivated');
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('patients')
        .update({
          status: 'active',
          lastactive: new Date().toISOString()
        })
        .eq('id', patientId);

      if (error) throw error;

      console.log('🏥 Patient reactivated successfully');
      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to reactivate patient', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get patient by access code
   */
  async getPatientByAccessCode(accessCode) {
    if (!this.isEnabled) {
      // Mock data
      if (accessCode === '847392') {
        return {
          id: 'mock_patient_1',
          firstName: 'John',
          lastInitial: 'D',
          roomNumber: '301A',
          accessCode: '847392'
        };
      }
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('access_code', accessCode)
        .single();

      if (error) throw error;

      return data;

    } catch (error) {
      console.error('Patient Service: Failed to get patient', error);
      return null;
    }
  }

  /**
   * Get patient therapy frequency
   */
  async getPatientTherapyFrequency(patientId) {
    if (!this.isEnabled) {
      // Mock data - return default frequency
      return '15 min/day × 7 days/week';
    }

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('therapy_frequency')
        .eq('id', patientId)
        .single();

      if (error) throw error;

      // Return the therapy frequency or default if not set
      return data?.therapy_frequency || '15 min/day × 7 days/week';
    } catch (error) {
      console.error('Patient Service: Failed to get therapy frequency', error);
      // Return default frequency on error
      return '15 min/day × 7 days/week';
    }
  }

  /**
   * Create exercise assignments for a patient
   */
  async createExerciseAssignments(patientId, clinicianId, exercises) {
    if (!this.isEnabled) {
      console.log('🏋️ Mock mode: Exercise assignments created');
      return { success: true, assignmentIds: ['mock_1', 'mock_2'] };
    }

    try {
      // First, delete all existing active assignments for this patient
      // Using delete instead of update to avoid duplicates
      const { error: deleteError } = await supabase
        .from('exercise_assignments')
        .delete()
        .eq('patient_id', patientId)
        .eq('status', 'active');

      if (deleteError) {
        console.warn('Warning: Could not remove old assignments:', deleteError);
      }

      // Now create the new assignments
      const assignments = exercises.map(exercise => ({
        patient_id: patientId,
        clinician_id: clinicianId,
        exercise_id: exercise.id,
        exercise_name: exercise.name,
        exercise_type: exercise.type,
        frequency: exercise.frequency || 'daily',
        duration_weeks: exercise.duration || 2,
        target_reps: exercise.targetReps || 10,
        status: 'active',
        notes: exercise.notes || ''
      }));

      const { data, error } = await supabase
        .from('exercise_assignments')
        .insert(assignments)
        .select();

      if (error) throw error;

      console.log('🏋️ Exercise assignments replaced:', data);
      return { success: true, assignments: data };

    } catch (error) {
      console.error('Patient Service: Failed to create assignments', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update patient's assigned exercise count
   */
  async updatePatientExerciseCount(patientId, count) {
    if (!this.isEnabled) {
      console.log('🏋️ Mock mode: Exercise count updated');
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('patients')
        .update({ assignedexercises: count })
        .eq('id', patientId);

      if (error) throw error;

      console.log('🏋️ Updated exercise count for patient:', patientId, 'Count:', count);
      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to update exercise count', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Update patient's therapy frequency
   */
  async updatePatientTherapyFrequency(patientId, frequency) {
    if (!this.isEnabled) {
      console.log('🗓️ Mock mode: Therapy frequency updated for patient:', patientId, 'Frequency:', frequency);
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('patients')
        .update({ therapy_frequency: frequency })
        .eq('id', patientId);

      if (error) throw error;

      console.log('🗓️ Updated therapy frequency for patient:', patientId, 'Frequency:', frequency);
      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to update therapy frequency', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get exercise assignments for a patient
   */
  async getPatientAssignments(patientId) {
    if (!this.isEnabled) {
      // Mock data
      return [
        {
          id: 'mock_1',
          exercise_name: 'Apple Pick',
          exercise_type: 'Upper Body',
          frequency: '15 min/day × 7 days/week',
          target_reps: 10,
          completed_reps: 5,
          status: 'active'
        },
        {
          id: 'mock_2',
          exercise_name: 'Super Punch',
          exercise_type: 'Upper Body',
          frequency: '20 min/day × 3 days/week',
          target_reps: 15,
          completed_reps: 8,
          status: 'active'
        }
      ];
    }

    try {
      const { data, error } = await supabase
        .from('exercise_assignments')
        .select('*')
        .eq('patient_id', patientId)
        .eq('status', 'active')
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      return data || [];

    } catch (error) {
      console.error('Patient Service: Failed to get assignments', error);
      return [];
    }
  }

  /**
   * Update assignment progress
   */
  async updateAssignmentProgress(assignmentId, completedReps) {
    if (!this.isEnabled) {
      console.log('🏋️ Mock mode: Assignment progress updated');
      return { success: true };
    }

    try {
      const { error } = await supabase
        .from('exercise_assignments')
        .update({
          completed_reps: completedReps,
          status: completedReps >= 10 ? 'completed' : 'active', // Mark as completed if target reached
          completed_at: completedReps >= 10 ? new Date().toISOString() : null
        })
        .eq('id', assignmentId);

      if (error) throw error;

      console.log('🏋️ Assignment progress updated');
      return { success: true };

    } catch (error) {
      console.error('Patient Service: Failed to update progress', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * Get patient analytics (detailed game metrics)
   */
  async getPatientAnalytics(patientId, dateRange = '7d') {
    // Ensure patientId is a string for consistency
    const patientIdStr = String(patientId);
    console.log('📊 PatientService.getPatientAnalytics called with:', {
      originalPatientId: patientId,
      patientIdStr,
      patientIdType: typeof patientId,
      dateRange,
      isEnabled: this.isEnabled,
      isMockPatient: patientIdStr.startsWith('mock_')
    });

    // For mock patients or disabled analytics, check localStorage for any recorded sessions
    if (!this.isEnabled || patientIdStr.startsWith('mock_')) {
      console.log('📊 PatientService: Mock mode or analytics disabled, checking localStorage');

      // Try to get mock analytics from localStorage
      const mockAnalyticsKey = `mock_analytics_${patientIdStr}`;
      const storedAnalytics = localStorage.getItem(mockAnalyticsKey);

      if (storedAnalytics) {
        try {
          const parsed = JSON.parse(storedAnalytics);
          console.log('📊 Found mock analytics in localStorage:', parsed);
          return parsed;
        } catch (e) {
          console.error('📊 Failed to parse mock analytics:', e);
        }
      }

      // Return empty to trigger placeholder
      console.log('📊 No mock analytics found, returning empty');
      return {
        sessions: [],
        summary: {}
      };
    }

    try {
      // Calculate date range
      let startDate = new Date();
      if (dateRange === 'all') {
        startDate = new Date('2000-01-01'); // Far past date to get all records
      } else if (dateRange === '24h') {
        startDate.setDate(startDate.getDate() - 1);
      } else if (dateRange === '7d') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (dateRange === '30d') {
        startDate.setDate(startDate.getDate() - 30);
      }

      // Fetch detailed game analytics
      console.log('📊 Querying game_analytics with patientIdStr:', patientIdStr, 'Type:', typeof patientIdStr, 'startDate:', startDate.toISOString());

      // Debug: First check what data exists in the table
      const { data: allRecords } = await supabase
        .from('game_analytics')
        .select('patient_id, game_name, start_time, form_score, reps_completed')
        .order('start_time', { ascending: false })
        .limit(10);

      console.log('📊 Recent game_analytics records in DB:', allRecords);
      if (allRecords && allRecords.length > 0) {
        console.log('📊 Unique patient IDs in database:', [...new Set(allRecords.map(r => r.patient_id))]);
        console.log('📊 Querying for patient ID:', patientIdStr, 'Type:', typeof patientIdStr);

        // Check if there's any data for similar IDs
        const similarIds = allRecords.filter(r =>
          r.patient_id == patientIdStr ||
          r.patient_id === String(patientIdStr) ||
          r.patient_id === Number(patientIdStr)
        );
        if (similarIds.length > 0) {
          console.log('📊 Found similar patient IDs:', similarIds);
        }
      }

      // Query with string version of the ID (consistent with how we store it)
      // First try with date filter
      console.log('📊 Querying game_analytics with params:', {
        table: 'game_analytics',
        patient_id: patientIdStr,
        start_time_gte: startDate.toISOString()
      });

      let { data: sessions, error: sessionsError } = await supabase
        .from('game_analytics')
        .select('*')
        .eq('patient_id', patientIdStr)
        .gte('start_time', startDate.toISOString())
        .order('start_time', { ascending: false })
        .limit(50);

      // If no results with date filter, try without date filter to get all data
      if ((!sessions || sessions.length === 0) && !sessionsError) {
        console.log('📊 No sessions found with date filter, trying without date filter');
        const { data: allSessions, error: allError } = await supabase
          .from('game_analytics')
          .select('*')
          .eq('patient_id', patientIdStr)
          .order('start_time', { ascending: false })
          .limit(50);

        if (allSessions && allSessions.length > 0) {
          console.log('📊 Found', allSessions.length, 'sessions without date filter');
          sessions = allSessions;
          sessionsError = allError;
        } else {
          // FALLBACK: Check if sessions were stored with user_id instead of patient_id
          console.log('📊 Checking for orphaned sessions with user_id...');
          const { data: patientData } = await supabase
            .from('patients')
            .select('user_id')
            .eq('id', patientIdStr)
            .single();
          
          if (patientData?.user_id) {
            console.log('📊 Trying query with user_id:', patientData.user_id);
            const { data: userSessions, error: userError } = await supabase
              .from('game_analytics')
              .select('*')
              .eq('patient_id', patientData.user_id)
              .order('start_time', { ascending: false })
              .limit(50);
            
            if (userSessions && userSessions.length > 0) {
              console.log('⚠️ Found', userSessions.length, 'sessions stored with user_id instead of patient_id!');
              sessions = userSessions;
              sessionsError = userError;
            }
          }
        }
      }

      console.log('📊 Game analytics query result:', {
        patientIdQueried: patientIdStr,
        sessionsCount: sessions?.length || 0,
        sessions: sessions?.slice(0, 2), // Log first 2 sessions for debugging
        allPatientIds: sessions ? [...new Set(sessions.map(s => s.patient_id))] : [],
        error: sessionsError
      });

      if (sessionsError) throw sessionsError;

      // Calculate summary metrics
      const summary = {
        total_sessions: sessions?.length || 0,
        total_reps: sessions?.reduce((sum, s) => sum + (s.reps_completed || 0), 0) || 0,
        avg_form_score: sessions?.length > 0
          ? sessions.reduce((sum, s) => sum + (s.form_score || 0), 0) / sessions.length
          : 0,
        avg_rom_percentage: sessions?.length > 0
          ? sessions.reduce((sum, s) => sum + (s.rom_percentage || 0), 0) / sessions.length
          : 0
      };

      // Log if no sessions found for this patient
      if (!sessions || sessions.length === 0) {
        console.log('📊 No sessions found for this specific patient ID:', patientIdStr);
        console.log('📊 Returning empty to trigger placeholder data');
      }

      const result = {
        sessions: sessions || [],
        summary
      };

      console.log('📊 PatientService returning:', {
        sessionCount: result.sessions.length,
        firstSession: result.sessions[0],
        summary: result.summary
      });

      return result;

    } catch (error) {
      console.error('Patient Service: Failed to get analytics', error);
      return { sessions: [], summary: {} };
    }
  }

  /**
   * Get platform sessions for a patient
   */
  async getPatientSessions(patientId, dateRange = '7d') {
    // Ensure patientId is a string for consistency
    const patientIdStr = String(patientId);
    if (!this.isEnabled) {
      return [
        {
          session_start: new Date(),
          session_end: new Date(),
          session_duration_seconds: 1800
        }
      ];
    }

    try {
      // First get the patient's name
      const { data: patient, error: patientError } = await supabase
        .from('patients')
        .select('firstName, firstname')
        .eq('id', patientIdStr)
        .single();

      if (patientError) throw patientError;
      
      const patientName = patient?.firstName || patient?.firstname;
      if (!patientName) {
        console.warn('Patient Service: No name found for patient', patientIdStr);
        return [];
      }

      let startDate = new Date();
      if (dateRange === 'all') {
        startDate = new Date('2000-01-01'); // Far past date to get all records
      } else if (dateRange === '24h') {
        startDate.setDate(startDate.getDate() - 1);
      } else if (dateRange === '7d') {
        startDate.setDate(startDate.getDate() - 7);
      } else if (dateRange === '30d') {
        startDate.setDate(startDate.getDate() - 30);
      }

      // Query user_sessions by patient name
      const { data, error } = await supabase
        .from('user_sessions')
        .select('*')
        .eq('user_name', patientName)
        .gte('session_start', startDate.toISOString())
        .order('session_start', { ascending: false });

      if (error) throw error;

      // Calculate duration for active sessions (not yet logged out)
      const sessionsWithDuration = (data || []).map(session => {
        if (session.session_duration_seconds) {
          // Completed session
          return session;
        } else if (session.session_start) {
          // Active session - calculate current duration
          const startTime = new Date(session.session_start);
          const now = new Date();
          const durationSeconds = Math.floor((now - startTime) / 1000);
          return {
            ...session,
            session_duration_seconds: durationSeconds
          };
        }
        return session;
      });

      console.log('📊 Patient sessions fetched:', sessionsWithDuration.length, 'sessions for', patientName);
      return sessionsWithDuration;

    } catch (error) {
      console.error('Patient Service: Failed to get platform sessions', error);
      return [];
    }
  }

  /**
   * Get patient analytics with previous period comparison
   * Returns both current and previous period data for trend analysis
   */
  async getPatientAnalyticsWithComparison(patientId, dateRange = '7d') {
    const patientIdStr = String(patientId);
    console.log('📊 PatientService.getPatientAnalyticsWithComparison:', { patientIdStr, dateRange });

    // For mock patients, return empty comparison
    if (!this.isEnabled || patientIdStr.startsWith('mock_')) {
      return {
        current: { sessions: [], summary: {} },
        previous: { sessions: [], summary: {} },
        canCompare: false,
        reason: 'mock_mode'
      };
    }

    try {
      const now = new Date();
      let currentStart, currentEnd, previousStart, previousEnd;
      let isAllTime = false;

      // Calculate date ranges for current and previous periods
      if (dateRange === 'all') {
        // All Time: fetch everything, no comparison
        isAllTime = true;
        currentEnd = now;
        currentStart = new Date('2000-01-01'); // Far past date to get all records
        previousStart = null;
        previousEnd = null;
      } else if (dateRange === '24h') {
        currentEnd = now;
        currentStart = new Date(now.getTime() - 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        previousStart = new Date(previousEnd.getTime() - 24 * 60 * 60 * 1000);
      } else if (dateRange === '7d') {
        currentEnd = now;
        currentStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        previousStart = new Date(previousEnd.getTime() - 7 * 24 * 60 * 60 * 1000);
      } else if (dateRange === '30d') {
        currentEnd = now;
        currentStart = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        previousEnd = currentStart;
        previousStart = new Date(previousEnd.getTime() - 30 * 24 * 60 * 60 * 1000);
      }

      console.log('📊 Date ranges:', {
        isAllTime,
        current: { start: currentStart.toISOString(), end: currentEnd.toISOString() },
        previous: previousStart ? { start: previousStart.toISOString(), end: previousEnd.toISOString() } : null
      });

      // Fetch both periods in parallel (or just current for all time)
      let currentData, previousData;
      if (isAllTime) {
        currentData = await this.fetchAnalyticsForPeriod(patientIdStr, currentStart, currentEnd);
        previousData = { sessions: [], summary: {} };
      } else {
        [currentData, previousData] = await Promise.all([
          this.fetchAnalyticsForPeriod(patientIdStr, currentStart, currentEnd),
          this.fetchAnalyticsForPeriod(patientIdStr, previousStart, previousEnd)
        ]);
      }

      // Check if we can make a valid comparison
      const canCompare = previousData.sessions.length > 0;
      const reason = !canCompare ? 'insufficient_history' : null;

      console.log('📊 Comparison result:', {
        currentSessions: currentData.sessions.length,
        previousSessions: previousData.sessions.length,
        canCompare,
        reason
      });

      return {
        current: currentData,
        previous: previousData,
        canCompare,
        reason
      };

    } catch (error) {
      console.error('PatientService: Failed to get analytics comparison', error);
      return {
        current: { sessions: [], summary: {} },
        previous: { sessions: [], summary: {} },
        canCompare: false,
        reason: 'error'
      };
    }
  }

  /**
   * Helper: Fetch analytics for a specific date range
   */
  async fetchAnalyticsForPeriod(patientIdStr, startDate, endDate) {
    try {
      const { data: sessions, error } = await supabase
        .from('game_analytics')
        .select('*')
        .eq('patient_id', patientIdStr)
        .gte('start_time', startDate.toISOString())
        .lte('start_time', endDate.toISOString())
        .order('start_time', { ascending: false });

      if (error) throw error;

      // Calculate summary metrics
      const summary = {
        total_sessions: sessions?.length || 0,
        total_reps: sessions?.reduce((sum, s) => sum + (s.reps_completed || 0), 0) || 0,
        total_time: sessions?.reduce((sum, s) => sum + (s.duration_seconds || 0), 0) || 0,
        avg_form_score: sessions?.length > 0
          ? sessions.reduce((sum, s) => sum + (s.form_score || 0), 0) / sessions.length
          : 0,
        avg_rom_percentage: sessions?.length > 0
          ? sessions.reduce((sum, s) => sum + (s.rom_percentage || 0), 0) / sessions.length
          : 0
      };

      return {
        sessions: sessions || [],
        summary
      };

    } catch (error) {
      console.error('PatientService: Failed to fetch period analytics', error);
      return { sessions: [], summary: {} };
    }
  }

  /**
   * Get daily summaries for a patient
   */
  async getDailySummaries(patientId, startDate, endDate) {
    // Ensure patientId is a string for consistency
    const patientIdStr = String(patientId);
    // Return empty data to trigger placeholder in PatientAnalyticsView
    if (!this.isEnabled) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('daily_summaries')
        .select('*')
        .eq('patient_id', patientIdStr)
        .gte('summary_date', startDate)
        .lte('summary_date', endDate)
        .order('summary_date', { ascending: false });

      if (error) throw error;

      return data || [];

    } catch (error) {
      console.error('Patient Service: Failed to get daily summaries', error);
      return [];
    }
  }

  /**
   * SOPHISTICATED: Track game completion for assigned exercises
   * Checks if the game is an assigned exercise and updates assignment progress
   * Only counts completions for assigned exercises within the therapy frequency window
   */
  async trackAssignmentCompletion(patientId, gameName, repsCompleted = 0) {
    const patientIdStr = String(patientId);
    console.log('🎯 trackAssignmentCompletion called:', { patientId: patientIdStr, gameName, repsCompleted });

    if (!this.isEnabled) {
      console.log('🎯 Mock mode: Assignment completion tracked');
      return { success: true, assignmentUpdated: false };
    }

    try {
      // STEP 1: Map game name to exercise type (handle variations)
      const gameToExerciseMap = {
        'Apple Pick': ['Apple Pick', 'Apple', 'apple'],
        'Super Punch': ['Super Punch', 'Punch', 'punch'],
        'Hot Potato': ['Hot Potato', 'Potato', 'potato'],
        'Tomato Dodge': ['Tomato Dodge', 'Tomato', 'tomato'],
        'Ocean Row': ['Ocean Row', 'Row', 'row'],
        'Sky Flight': ['Sky Flight', 'Sky', 'sky'],
        'Plant Care': ['Plant Care', 'Garden', 'garden'],
        'Berry Harvest': ['Berry Harvest', 'Berry', 'berry']
      };

      // Find matching exercise name
      let exerciseName = gameName;
      for (const [standardName, variations] of Object.entries(gameToExerciseMap)) {
        if (variations.some(v => gameName.toLowerCase().includes(v.toLowerCase()))) {
          exerciseName = standardName;
          break;
        }
      }

      console.log('🎯 Mapped game name:', gameName, '→', exerciseName);

      // STEP 2: Get active assignments for this patient and exercise
      const { data: assignments, error: assignmentError } = await supabase
        .from('exercise_assignments')
        .select('*')
        .eq('patient_id', patientIdStr)
        .eq('status', 'active')
        .ilike('exercise_name', `%${exerciseName}%`);

      if (assignmentError) throw assignmentError;

      console.log('🎯 Found assignments:', assignments?.length || 0, assignments);

      if (!assignments || assignments.length === 0) {
        console.log('🎯 No active assignment found for this exercise');
        return { success: true, assignmentUpdated: false, reason: 'not_assigned' };
      }

      // STEP 3: Update the assignment progress
      const assignment = assignments[0]; // Use the first matching assignment
      const currentCompleted = assignment.completed_reps || 0;
      const targetReps = assignment.target_reps || 10;
      const newCompletedCount = currentCompleted + 1; // Increment session count

      // Check if assignment is now completed
      const isNowCompleted = newCompletedCount >= targetReps;

      console.log('🎯 Updating assignment:', {
        assignmentId: assignment.id,
        current: currentCompleted,
        new: newCompletedCount,
        target: targetReps,
        isNowCompleted
      });

      // Update assignment progress
      const { error: updateError } = await supabase
        .from('exercise_assignments')
        .update({
          completed_reps: newCompletedCount,
          status: isNowCompleted ? 'completed' : 'active',
          completed_at: isNowCompleted ? new Date().toISOString() : null
        })
        .eq('id', assignment.id);

      if (updateError) throw updateError;

      console.log('🎯 Assignment progress updated successfully');

      // STEP 4: Recalculate total completed exercises for patient
      await this.recalculatePatientCompletions(patientIdStr);

      return {
        success: true,
        assignmentUpdated: true,
        isNowCompleted,
        completedCount: newCompletedCount,
        targetReps
      };

    } catch (error) {
      console.error('PatientService: Failed to track assignment completion', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * SOPHISTICATED: Recalculate patient's completed exercises count
   * Counts only completed assignments (not raw game plays)
   * This ensures accurate tracking based on therapy frequency and assigned exercises
   */
  async recalculatePatientCompletions(patientId) {
    const patientIdStr = String(patientId);
    console.log('🔄 recalculatePatientCompletions called for patient:', patientIdStr);

    if (!this.isEnabled) {
      return { success: true };
    }

    try {
      // Count completed assignments for this patient
      const { data: completedAssignments, error: countError } = await supabase
        .from('exercise_assignments')
        .select('id', { count: 'exact', head: true })
        .eq('patient_id', patientIdStr)
        .eq('status', 'completed');

      if (countError) throw countError;

      const completedCount = completedAssignments || 0;

      console.log('🔄 Completed assignments count:', completedCount);

      // Update patient record with accurate count
      const { error: updateError } = await supabase
        .from('patients')
        .update({
          completedexercises: completedCount,
          lastactive: new Date().toISOString()
        })
        .eq('id', patientIdStr);

      if (updateError) throw updateError;

      console.log('🔄 Patient completion count updated to:', completedCount);

      return { success: true, completedCount };

    } catch (error) {
      console.error('PatientService: Failed to recalculate completions', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * SOPHISTICATED: Get assignment-based progress summary
   * Shows which assigned exercises are completed vs in-progress
   */
  async getAssignmentProgress(patientId) {
    const patientIdStr = String(patientId);

    if (!this.isEnabled) {
      return {
        total: 0,
        completed: 0,
        inProgress: 0,
        assignments: []
      };
    }

    try {
      const { data: assignments, error } = await supabase
        .from('exercise_assignments')
        .select('*')
        .eq('patient_id', patientIdStr)
        .order('assigned_at', { ascending: false });

      if (error) throw error;

      const completed = assignments?.filter(a => a.status === 'completed').length || 0;
      const inProgress = assignments?.filter(a => a.status === 'active').length || 0;

      return {
        total: assignments?.length || 0,
        completed,
        inProgress,
        assignments: assignments || []
      };

    } catch (error) {
      console.error('PatientService: Failed to get assignment progress', error);
      return {
        total: 0,
        completed: 0,
        inProgress: 0,
        assignments: []
      };
    }
  }

  /**
   * Get game performance metrics for a patient
   */
  async getGamePerformance(patientId, gameName = null) {
    // Ensure patientId is a string for consistency
    const patientIdStr = String(patientId);
    // Return empty data to trigger placeholder in PatientAnalyticsView
    if (!this.isEnabled) {
      return {};
    }

    try {
      let query = supabase
        .from('game_performance_by_patient')
        .select('*')
        .eq('patient_id', patientIdStr);

      if (gameName) {
        query = query.eq('game_name', gameName);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Game name mapping to fix inconsistent names in database
      const gameNameMapping = {
        'Berry': 'Berry Harvest',
        'Row': 'Ocean Row',
        'Physics': 'Sky Flight',  // Assuming Physics is a misnamed Sky Flight
        'Apple': 'Apple Pick',
        'Punch': 'Super Punch',
        'Potato': 'Hot Potato',
        'Tomato': 'Tomato Dodge',
        'Sky': 'Sky Flight',
        'Plant': 'Plant Care',
        'Garden': 'Plant Care',
        'Ocean': 'Ocean Row',
        'Super': 'Super Punch',
        'Hot': 'Hot Potato'
      };

      // Convert to object format for easier access
      const performance = {};
      (data || []).forEach(game => {
        // Normalize game name - check if it needs mapping
        let normalizedGameName = game.game_name;

        // First check for exact match in mapping
        if (gameNameMapping[game.game_name]) {
          normalizedGameName = gameNameMapping[game.game_name];
        }
        // If not found, check if it's already a full name
        else if (!['Apple Pick', 'Super Punch', 'Hot Potato', 'Tomato Dodge',
                   'Ocean Row', 'Sky Flight', 'Plant Care', 'Berry Harvest'].includes(game.game_name)) {
          // Try to find a match by checking if the game_name is part of a key
          for (const [shortName, fullName] of Object.entries(gameNameMapping)) {
            if (game.game_name.includes(shortName) || shortName.includes(game.game_name)) {
              normalizedGameName = fullName;
              break;
            }
          }
        }

        performance[normalizedGameName] = {
          sessions: game.times_played,
          avgFormScore: game.avg_form_score,
          avgROM: game.avg_rom,
          totalReps: game.total_reps,
          completions: game.completions,
          abandonments: game.abandonments
        };
      });

      return performance;

    } catch (error) {
      console.error('Patient Service: Failed to get game performance', error);
      return {};
    }
  }

  /**
   * Get all patients for a specific clinician (for SuperAdmin dashboard)
   */
  async getPatientsByClinicianId(clinicianId) {
    if (!this.isEnabled) {
      console.warn('Patient Service: Supabase not configured');
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('patients')
        .select('*')
        .eq('clinician_id', clinicianId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log(`🏥 Loaded ${data?.length || 0} patients for clinician: ${clinicianId}`);
      return data || [];

    } catch (error) {
      console.error('Patient Service: Failed to get patients by clinician', error);
      return [];
    }
  }
}

// Create singleton instance
const patientService = new PatientService();
export default patientService;